import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { helpdeskMetricsAPI, helpdeskSLAAPI, helpdeskReportsAPI, periodsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Progress } from '../../components/ui/progress';
import { Checkbox } from '../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Ticket,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  Settings,
  Timer,
  Target,
  CalendarDays,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Hash,
  X,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
} from 'recharts';

const QUARTER_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: '1', label: 'T1 (Ene-Mar)' },
  { value: '2', label: 'T2 (Abr-Jun)' },
  { value: '3', label: 'T3 (Jul-Sep)' },
  { value: '4', label: 'T4 (Oct-Dic)' },
];

const AREA_FILTER_OPTIONS = [
  { value: 'all', label: 'Todas las áreas' },
  { value: 'Soporte Técnico', label: 'Soporte Técnico' },
  { value: 'Sistemas', label: 'Sistemas' },
  { value: 'Infraestructura', label: 'Infraestructura' },
];

const PRIORITY_LABELS = { low: 'Baja', medium: 'Media', high: 'Alta' };
const STATUS_LABELS = { open: 'Abiertos', in_progress: 'En proceso', on_hold: 'En espera', scheduled: 'Programados', closed: 'Cerrados' };
const STATUS_COLORS = { open: '#3b82f6', in_progress: '#f59e0b', on_hold: '#94a3b8', scheduled: '#8b5cf6', closed: '#10b981' };
const PRIORITY_COLORS = { low: '#94a3b8', medium: '#f59e0b', high: '#ef4444' };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
      <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: <span className="font-semibold">{entry.value}</span>
        </p>
      ))}
    </div>
  );
};

const KPICard = ({ icon: Icon, iconBg, iconColor, value, label, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={cn("p-2 rounded-lg", iconBg)}>
          <Icon className={cn("w-5 h-5", iconColor)} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-2xl font-bold font-['Manrope'] truncate">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const HelpdeskReports = () => {
  const { isAdmin } = useAuth();
  const { isHelpdeskAdmin } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [report, setReport] = useState(null);
  const [csat, setCsat] = useState(null);
  const [slaConfig, setSlaConfig] = useState({ low_hours: 10, medium_hours: 6, high_hours: 2 });

  // Filters
  const [selectedYear, setSelectedYear] = useState(2026);
  const [perPeriods, setPerPeriods] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [selectedPer, setSelectedPer] = useState('');
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [reportAgents, setReportAgents] = useState([]);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [appliedRange, setAppliedRange] = useState(null);
  const [rangeError, setRangeError] = useState('');

  // SLA Config dialog
  const [isSlaDialogOpen, setIsSlaDialogOpen] = useState(false);
  const [slaForm, setSlaForm] = useState({ low_hours: 10, medium_hours: 6, high_hours: 2 });
  const [savingSla, setSavingSla] = useState(false);

  const fetchReport = useCallback(async (perId = '', qtr = '', year = 2026, extra = {}) => {
    setReportLoading(true);
    try {
      const base = { year };
      if (extra.range) {
        base.start_date = extra.range.start;
        base.end_date = extra.range.end;
      } else if (perId) base.period_id = perId;
      else if (qtr && qtr !== 'all') base.quarter = parseInt(qtr);
      if (extra.area?.length) base.area = extra.area.join(',');
      if (extra.agent && extra.agent !== 'all') base.agent_id = extra.agent;
      const [res, csatRes] = await Promise.all([
        helpdeskReportsAPI.getPeriodReport({ period_type: 'weekly', ...base }),
        helpdeskReportsAPI.getCsatReport(base),
      ]);
      setReport(res.data);
      setCsat(csatRes.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error(error.response?.data?.detail || 'Error al cargar el reporte');
    } finally {
      setReportLoading(false);
    }
  }, []);

  // Área / Agente / Rango vigentes, para combinarse con Trimestre/PER
  const extraFilters = (overrides = {}) => ({
    area: selectedAreas, agent: selectedAgent, range: appliedRange, ...overrides,
  });

  useEffect(() => {
    const fetchInitial = async () => {
      setLoading(true);
      try {
        const [metricsRes, slaRes, periodsRes, currentRes] = await Promise.all([
          helpdeskMetricsAPI.getMetrics(),
          helpdeskSLAAPI.getConfig(),
          periodsAPI.getAll({ year: 2026 }),
          periodsAPI.getCurrent(),
        ]);
        setMetrics(metricsRes.data);
        setSlaConfig(slaRes.data);
        setSlaForm(slaRes.data);
        setPerPeriods(periodsRes.data || []);
        setCurrentPeriod(currentRes.data);
        // Default: current quarter
        const currentMonth = new Date().getMonth() + 1;
        const defaultQ = String(Math.ceil(currentMonth / 3));
        setSelectedQuarter(defaultQ);
        fetchReport('', defaultQ, 2026);
        helpdeskReportsAPI.getAgents()
          .then(res => setReportAgents(res.data || []))
          .catch(() => {});
      } catch (error) {
        console.error('Error fetching initial data:', error);
        toast.error('Error al cargar los informes');
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
  }, [fetchReport]);

  // Refetch periods when year changes
  useEffect(() => {
    periodsAPI.getAll({ year: selectedYear })
      .then(res => setPerPeriods(res.data || []))
      .catch(() => {});
  }, [selectedYear]);

  const clearRange = () => {
    setAppliedRange(null);
    setRangeStart('');
    setRangeEnd('');
    setRangeError('');
  };

  const handlePerSelect = (val) => {
    clearRange();
    const extra = extraFilters({ range: null });
    if (val === 'all') {
      setSelectedPer('');
      // Restore quarter if none active
      if (!selectedQuarter || selectedQuarter === 'all') {
        const currentMonth = new Date().getMonth() + 1;
        const defaultQ = String(Math.ceil(currentMonth / 3));
        setSelectedQuarter(defaultQ);
        fetchReport('', defaultQ, selectedYear, extra);
      } else {
        fetchReport('', selectedQuarter, selectedYear, extra);
      }
      return;
    }
    setSelectedPer(val);
    setSelectedQuarter('all');
    fetchReport(val, '', selectedYear, extra);
  };

  const handleQuarterSelect = (val) => {
    clearRange();
    setSelectedQuarter(val);
    setSelectedPer('');
    fetchReport('', val, selectedYear, extraFilters({ range: null }));
  };

  // Multi-área: [] = Todas las áreas
  const handleAreaToggle = (val) => {
    const next = val === 'all'
      ? []
      : selectedAreas.includes(val) ? selectedAreas.filter(a => a !== val) : [...selectedAreas, val];
    setSelectedAreas(next);
    fetchReport(selectedPer, selectedQuarter, selectedYear, extraFilters({ area: next }));
  };

  const handleAgentSelect = (val) => {
    setSelectedAgent(val);
    fetchReport(selectedPer, selectedQuarter, selectedYear, extraFilters({ agent: val }));
  };

  const handleApplyRange = () => {
    if (!rangeStart || !rangeEnd) {
      setRangeError('Selecciona fecha inicio y fecha fin');
      return;
    }
    if (rangeStart > rangeEnd) {
      setRangeError('La fecha inicio debe ser menor o igual a la fecha fin');
      return;
    }
    setRangeError('');
    const range = { start: rangeStart, end: rangeEnd };
    setAppliedRange(range);
    setSelectedQuarter('');
    setSelectedPer('');
    fetchReport('', '', selectedYear, extraFilters({ range }));
  };

  const handleClearRange = () => {
    clearRange();
    const currentMonth = new Date().getMonth() + 1;
    const defaultQ = String(Math.ceil(currentMonth / 3));
    setSelectedQuarter(defaultQ);
    fetchReport('', defaultQ, selectedYear, extraFilters({ range: null }));
  };

  const handleSaveSla = async (e) => {
    e.preventDefault();
    setSavingSla(true);
    try {
      await helpdeskSLAAPI.updateConfig(slaForm);
      setSlaConfig(slaForm);
      toast.success('Configuracion SLA actualizada');
      setIsSlaDialogOpen(false);
    } catch (error) {
      console.error('Error saving SLA:', error);
      toast.error('Error al guardar la configuracion');
    } finally {
      setSavingSla(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-80" />
        </div>
      </Layout>
    );
  }

  if (!metrics) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">No se pudieron cargar los informes</p>
        </div>
      </Layout>
    );
  }

  // Trend chart data - use the label from backend (PER-aligned)
  const trendData = (report?.trend_data || []).map(d => ({
    ...d,
    displayLabel: d.label || d.date,
  }));

  // For weekly view, shorten label for chart axis (show PER## only)
  const getAxisLabel = (item) => {
    const match = item.displayLabel?.match(/^(PER\d+)/);
    return match ? match[1] : item.displayLabel;
  };

  // Area breakdown from report (PER-aligned)
  const areaData = Object.entries(report?.by_area || {})
    .map(([name, value]) => ({ name, tickets: value }))
    .sort((a, b) => b.tickets - a.tickets);

  // Priority pie from report
  const priorityData = Object.entries(report?.by_priority || {})
    .map(([key, value]) => ({ name: PRIORITY_LABELS[key] || key, value, color: PRIORITY_COLORS[key] || '#94a3b8' }))
    .filter(d => d.value > 0);

  // Status pie from report
  const statusData = Object.entries(report?.by_status || {})
    .map(([key, value]) => ({ name: STATUS_LABELS[key] || key, value, color: STATUS_COLORS[key] || '#94a3b8' }))
    .filter(d => d.value > 0);

  // Category data from global metrics
  const categoryData = Object.entries(metrics.tickets_by_category || {})
    .map(([name, value]) => ({ name, tickets: value }))
    .sort((a, b) => b.tickets - a.tickets);

  // Active filter label
  const periodLabel = appliedRange
    ? `${appliedRange.start} a ${appliedRange.end}`
    : selectedPer
    ? `PER ${perPeriods.find(p => p.id === selectedPer)?.number || '?'}`
    : selectedQuarter && selectedQuarter !== 'all'
    ? QUARTER_OPTIONS.find(q => q.value === selectedQuarter)?.label
    : 'Todos';
  const agentLabel = selectedAgent === 'unassigned'
    ? 'Sin asignar'
    : reportAgents.find(a => a.id === selectedAgent)?.name;
  const activeFilterLabel = [
    periodLabel,
    selectedAreas.length ? selectedAreas.join(' + ') : null,
    selectedAgent !== 'all' ? agentLabel : null,
  ].filter(Boolean).join(' · ');

  // Date range display
  const dateRangeStr = report ? `${report.start_date} a ${report.end_date}` : '';

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-800" data-testid="reports-title">
              Informes y Metricas
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Rendimiento de la Mesa de Ayuda alineado al calendario de periodos (PER)
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isHelpdeskAdmin && (
              <Button variant="outline" size="sm" onClick={() => setIsSlaDialogOpen(true)} data-testid="sla-config-btn">
                <Settings className="w-4 h-4 mr-2" />
                SLA
              </Button>
            )}
          </div>
        </div>

        {/* Global KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPICard
            icon={Ticket}
            iconBg="bg-slate-100"
            iconColor="text-slate-600"
            value={metrics.total_tickets}
            label="Total Tickets"
            testId="kpi-total-tickets"
          />
          <KPICard
            icon={AlertTriangle}
            iconBg="bg-rose-100"
            iconColor="text-rose-600"
            value={metrics.tickets_out_of_sla}
            label="Fuera de SLA"
            testId="kpi-out-of-sla"
          />
          <KPICard
            icon={Timer}
            iconBg="bg-blue-100"
            iconColor="text-blue-600"
            value={metrics.avg_first_response_hours != null ? `${metrics.avg_first_response_hours}h` : '-'}
            label="Prom. 1ra Respuesta"
            testId="kpi-avg-response"
          />
          <KPICard
            icon={CheckCircle2}
            iconBg="bg-emerald-100"
            iconColor="text-emerald-600"
            value={metrics.avg_resolution_hours != null ? `${metrics.avg_resolution_hours}h` : '-'}
            label="Prom. Resolucion"
            testId="kpi-avg-resolution"
          />
        </div>

        {/* SLA Compliance */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-slate-400" />
                <span className="text-sm font-medium text-slate-700">Cumplimiento SLA</span>
              </div>
              <div className="flex-1">
                <Progress
                  value={metrics.sla_compliance_percent}
                  className={cn(
                    "h-2.5",
                    metrics.sla_compliance_percent >= 90 ? "[&>div]:bg-emerald-500" :
                    metrics.sla_compliance_percent >= 70 ? "[&>div]:bg-amber-500" : "[&>div]:bg-rose-500"
                  )}
                />
              </div>
              <span className={cn(
                "text-lg font-bold font-['Manrope']",
                metrics.sla_compliance_percent >= 90 ? "text-emerald-600" :
                metrics.sla_compliance_percent >= 70 ? "text-amber-600" : "text-rose-600"
              )} data-testid="sla-compliance-value">
                {metrics.sla_compliance_percent}%
              </span>
              <span className="text-xs text-slate-400 hidden sm:block">
                Alta: {slaConfig.high_hours}h | Media: {slaConfig.medium_hours}h | Baja: {slaConfig.low_hours}h
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Period Report Section */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold font-['Manrope'] text-slate-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-slate-400" />
                Tendencia de Tickets
              </h2>
              {report && (
                <p className="text-xs text-slate-400 mt-1" data-testid="report-period-range">
                  Periodo: {report.start_date} a {report.end_date}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Year selector */}
              <Select value={selectedYear.toString()} onValueChange={(v) => {
                const yr = parseInt(v);
                setSelectedYear(yr);
                setSelectedPer('');
                fetchReport('', selectedQuarter, yr, extraFilters());
              }}>
                <SelectTrigger className="w-24" data-testid="year-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                </SelectContent>
              </Select>

              {/* Quarter / Trimestre selector */}
              <Select value={appliedRange ? '' : (selectedQuarter || 'all')} onValueChange={handleQuarterSelect}>
                <SelectTrigger className="w-[150px]" data-testid="quarter-selector">
                  <CalendarDays className="w-4 h-4 mr-1 text-slate-400" />
                  <SelectValue placeholder="Trimestre" />
                </SelectTrigger>
                <SelectContent>
                  {QUARTER_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} data-testid={`quarter-option-${opt.value}`}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* PER Period selector */}
              <Select value={appliedRange ? '' : (selectedPer || 'all')} onValueChange={handlePerSelect}>
                <SelectTrigger className="w-[160px]" data-testid="per-selector">
                  <Hash className="w-4 h-4 mr-1 text-slate-400" />
                  <SelectValue placeholder="Periodo PER" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los PER</SelectItem>
                  {perPeriods
                    .sort((a, b) => (a.number || 0) - (b.number || 0))
                    .map((p, i) => (
                    <SelectItem key={p.id} value={p.id}>
                      <div className="flex items-center gap-2">
                        <span>PER {p.number || i + 1}</span>
                        {currentPeriod?.id === p.id && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-emerald-50 text-emerald-700 border-emerald-200">
                            Actual
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Área selector */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex h-9 w-[190px] items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    data-testid="area-filter-selector"
                  >
                    <span className="truncate">
                      {selectedAreas.length === 0
                        ? 'Todas las áreas'
                        : selectedAreas.length === 1 ? selectedAreas[0] : `${selectedAreas.length} áreas`}
                    </span>
                    <ChevronDown className="w-4 h-4 opacity-50 flex-shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-56 p-2">
                  {AREA_FILTER_OPTIONS.map(opt => {
                    const checked = opt.value === 'all' ? selectedAreas.length === 0 : selectedAreas.includes(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm cursor-pointer hover:bg-slate-100"
                        data-testid={`area-filter-option-${opt.value}`}
                      >
                        <Checkbox checked={checked} onCheckedChange={() => handleAreaToggle(opt.value)} />
                        {opt.label}
                      </label>
                    );
                  })}
                </PopoverContent>
              </Popover>

              {/* Agente selector */}
              <Select value={selectedAgent} onValueChange={handleAgentSelect}>
                <SelectTrigger className="w-[170px]" data-testid="agent-filter-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="agent-filter-option-all">Todos los agentes</SelectItem>
                  {reportAgents.map(a => (
                    <SelectItem key={a.id} value={a.id} data-testid={`agent-filter-option-${a.id}`}>{a.name}</SelectItem>
                  ))}
                  <SelectItem value="unassigned" data-testid="agent-filter-option-unassigned">Sin asignar</SelectItem>
                </SelectContent>
              </Select>

              {/* Rango de fechas */}
              <div className="flex items-center gap-1">
                <Input
                  type="date"
                  value={rangeStart}
                  onChange={(e) => { setRangeStart(e.target.value); setRangeError(''); }}
                  className="w-[140px]"
                  data-testid="range-start-input"
                />
                <span className="text-xs text-slate-400">a</span>
                <Input
                  type="date"
                  value={rangeEnd}
                  onChange={(e) => { setRangeEnd(e.target.value); setRangeError(''); }}
                  className="w-[140px]"
                  data-testid="range-end-input"
                />
                <Button variant="outline" size="sm" onClick={handleApplyRange} data-testid="range-apply-btn">
                  Aplicar
                </Button>
                {appliedRange && (
                  <Button variant="ghost" size="icon" onClick={handleClearRange} data-testid="range-clear-btn">
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => fetchReport(selectedPer, selectedQuarter, selectedYear, extraFilters())}
                disabled={reportLoading}
                data-testid="refresh-report-btn"
              >
                <RefreshCw className={cn("w-4 h-4", reportLoading && "animate-spin")} />
              </Button>
            </div>
          </div>
          {rangeError && (
            <p className="text-xs text-rose-600 -mt-2 sm:text-right" data-testid="range-error">{rangeError}</p>
          )}

          {/* Period Summary Cards */}
          {report && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 mb-1">Creados</p>
                  <p className="text-2xl font-bold font-['Manrope'] text-blue-600" data-testid="report-total-created">
                    {report.total_created}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-emerald-500">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 mb-1">Cerrados</p>
                  <p className="text-2xl font-bold font-['Manrope'] text-emerald-600" data-testid="report-total-closed">
                    {report.total_closed}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-amber-500">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 mb-1">Abiertos</p>
                  <p className="text-2xl font-bold font-['Manrope'] text-amber-600" data-testid="report-total-open">
                    {report.total_open}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-violet-500">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 mb-1">Prom. Resolucion</p>
                  <p className="text-2xl font-bold font-['Manrope'] text-violet-600" data-testid="report-avg-resolution">
                    {report.avg_resolution_hours != null ? `${report.avg_resolution_hours}h` : '-'}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* SLA Compliance by Priority */}
          {report && report.sla_by_priority && (
            <div data-testid="sla-by-priority-section">
              <h2 className="text-lg font-semibold font-['Manrope'] text-slate-800 flex items-center gap-2 mb-4">
                <Target className="w-5 h-5 text-slate-400" />
                Cumplimiento SLA por Prioridad
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { key: 'high', label: 'Alta', limit: slaConfig.high_hours, color: 'rose' },
                  { key: 'medium', label: 'Media', limit: slaConfig.medium_hours, color: 'amber' },
                  { key: 'low', label: 'Baja', limit: slaConfig.low_hours, color: 'slate' },
                ].map(({ key, label, limit, color }) => {
                  const data = report.sla_by_priority[key] || { on_time: 0, late: 0 };
                  return (
                    <div key={key} className="space-y-3" data-testid={`sla-priority-${key}`}>
                      <p className={cn(
                        "text-sm font-semibold text-center py-1 rounded-md",
                        color === 'rose' && "bg-rose-50 text-rose-700",
                        color === 'amber' && "bg-amber-50 text-amber-700",
                        color === 'slate' && "bg-slate-100 text-slate-700",
                      )}>
                        {label} (&lt; {limit}h)
                      </p>
                      <Card className="border-l-4 border-l-emerald-500">
                        <CardContent className="p-4 flex items-center gap-3">
                          <div className="p-2 bg-emerald-50 rounded-lg">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold font-['Manrope'] text-emerald-600" data-testid={`sla-on-time-${key}`}>
                              {data.on_time}
                            </p>
                            <p className="text-xs text-slate-500">Resueltos a tiempo</p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="border-l-4 border-l-rose-500">
                        <CardContent className="p-4 flex items-center gap-3">
                          <div className="p-2 bg-rose-50 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-rose-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold font-['Manrope'] text-rose-600" data-testid={`sla-late-${key}`}>
                              {data.late}
                            </p>
                            <p className="text-xs text-slate-500">Resueltos fuera de tiempo</p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Trend Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">
                Tickets Creados vs Cerrados — {activeFilterLabel}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reportLoading ? (
                <Skeleton className="h-[280px] w-full" />
              ) : trendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={trendData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <defs>
                      <linearGradient id="gradCreated" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradClosed" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="displayLabel"
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      interval={1}
                      angle={-35}
                      textAnchor="end"
                      height={60}
                      tickFormatter={(val) => {
                        const match = val?.match(/^(PER\d+)/);
                        return match ? match[1] : val;
                      }}
                    />
                    <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        return (
                          <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
                            <p className="text-xs font-medium text-slate-600 mb-1">{label}</p>
                            {payload.map((entry, i) => (
                              <p key={i} className="text-sm" style={{ color: entry.color }}>
                                {entry.name}: <span className="font-semibold">{entry.value}</span>
                              </p>
                            ))}
                          </div>
                        );
                      }}
                    />
                    <Area type="monotone" dataKey="created" name="Creados" fill="url(#gradCreated)" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4, fill: '#3b82f6' }} />
                    <Area type="monotone" dataKey="closed" name="Cerrados" fill="url(#gradClosed)" stroke="#10b981" strokeWidth={2} dot={{ r: 4, fill: '#10b981' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-slate-400">
                  No hay datos de tendencia para este periodo
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Distribution Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Status Distribution */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">Por Estado</CardTitle>
            </CardHeader>
            <CardContent>
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-400 text-sm">
                  Sin datos
                </div>
              )}
            </CardContent>
          </Card>

          {/* Priority Distribution */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">Por Prioridad</CardTitle>
            </CardHeader>
            <CardContent>
              {priorityData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={priorityData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {priorityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-400 text-sm">
                  Sin datos
                </div>
              )}
            </CardContent>
          </Card>

          {/* By Area */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">Tickets por Area</CardTitle>
            </CardHeader>
            <CardContent>
              {areaData.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(200, areaData.length * 40)}>
                  <BarChart data={areaData} layout="vertical" margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="tickets" name="Tickets" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">
                  Sin datos
                </div>
              )}
            </CardContent>
          </Card>

          {/* By Category */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">Tickets por Categoria</CardTitle>
            </CardHeader>
            <CardContent>
              {categoryData.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(200, categoryData.length * 40)}>
                  <BarChart data={categoryData} layout="vertical" margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="tickets" name="Tickets" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">
                  Sin datos
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detailed Breakdown Table */}
        {report && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">
                Desglose del Periodo ({activeFilterLabel})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* By Area table */}
                <div>
                  <h3 className="text-sm font-medium text-slate-600 mb-3">Por Area</h3>
                  <div className="space-y-2">
                    {Object.entries(report.by_area || {})
                      .sort(([,a], [,b]) => b - a)
                      .map(([area, count]) => (
                        <div key={area} className="flex items-center justify-between" data-testid={`area-row-${area}`}>
                          <span className="text-sm text-slate-700 truncate max-w-[180px]">{area}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 rounded-full"
                                style={{ width: `${Math.min(100, (count / (report.total_created || 1)) * 100)}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-slate-800 w-8 text-right">{count}</span>
                          </div>
                        </div>
                    ))}
                    {Object.keys(report.by_area || {}).length === 0 && (
                      <p className="text-xs text-slate-400">Sin datos</p>
                    )}
                  </div>
                </div>

                {/* By Priority table */}
                <div>
                  <h3 className="text-sm font-medium text-slate-600 mb-3">Por Prioridad</h3>
                  <div className="space-y-2">
                    {Object.entries(report.by_priority || {})
                      .filter(([, v]) => v > 0)
                      .sort(([,a], [,b]) => b - a)
                      .map(([key, count]) => (
                        <div key={key} className="flex items-center justify-between" data-testid={`priority-row-${key}`}>
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PRIORITY_COLORS[key] }} />
                            <span className="text-sm text-slate-700">{PRIORITY_LABELS[key] || key}</span>
                          </div>
                          <span className="text-sm font-medium text-slate-800">{count}</span>
                        </div>
                    ))}
                  </div>
                </div>

                {/* By Status table */}
                <div>
                  <h3 className="text-sm font-medium text-slate-600 mb-3">Por Estado</h3>
                  <div className="space-y-2">
                    {Object.entries(report.by_status || {})
                      .filter(([, v]) => v > 0)
                      .sort(([,a], [,b]) => b - a)
                      .map(([key, count]) => (
                        <div key={key} className="flex items-center justify-between" data-testid={`status-row-${key}`}>
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[key] }} />
                            <span className="text-sm text-slate-700">{STATUS_LABELS[key] || key}</span>
                          </div>
                          <span className="text-sm font-medium text-slate-800">{count}</span>
                        </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* CSAT — Satisfacción */}
        {csat && (
          <Card data-testid="csat-report-section">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                Satisfacción (CSAT)
              </CardTitle>
              <p className="text-xs text-slate-500">
                Sobre {csat.total_closed} ticket(s) cerrado(s) en el período seleccionado
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {(() => {
                  const resolvedTotal = (csat.resolved || 0) + (csat.assumed_resolved || 0);
                  const pctOf = (n) => csat.total_closed > 0 ? Math.round((n / csat.total_closed) * 1000) / 10 : 0;
                  const cards = [
                    { key: 'total', label: 'Total cerrados', isTotal: true, n: csat.total_closed, color: 'text-slate-800' },
                    { key: 'resolved', label: 'Resueltos', pct: pctOf(resolvedTotal), n: resolvedTotal, color: 'text-emerald-600' },
                    { key: 'unresolved', label: 'No resueltos', pct: pctOf(csat.unresolved), n: csat.unresolved, color: 'text-red-600' },
                    { key: 'pending', label: 'Pendientes de calificar', pct: pctOf(csat.pending), n: csat.pending, color: 'text-amber-600' },
                  ];
                  return cards.map(item => (
                    <div key={item.key} className="border border-slate-200 rounded-lg p-3" data-testid={`csat-stat-${item.key}`}>
                      <div className={cn('text-2xl font-bold', item.color)}>{item.isTotal ? item.n : `${item.pct}%`}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{item.label}</div>
                      {!item.isTotal && <div className="text-xs text-slate-400 mt-1">{item.n} ticket(s)</div>}
                    </div>
                  ));
                })()}
              </div>

              <h3 className="text-sm font-medium text-slate-600 mb-2">Desglose por agente</h3>
              {csat.by_agent && csat.by_agent.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="csat-agent-table">
                    <thead>
                      <tr className="text-left text-slate-500 border-b border-slate-200">
                        <th className="py-2 font-medium">Agente</th>
                        <th className="py-2 font-medium text-right">Tickets cerrados</th>
                        <th className="py-2 font-medium text-right">% Resueltos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csat.by_agent.map((a, i) => (
                        <tr key={i} className="border-b border-slate-100" data-testid={`csat-agent-row-${i}`}>
                          <td className="py-2 text-slate-700">{a.agent}</td>
                          <td className="py-2 text-right text-slate-700">{a.closed}</td>
                          <td className="py-2 text-right font-medium text-slate-800">{a.csat_confirmed_pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-400 py-2">Sin datos en el período seleccionado.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* SLA Config Dialog */}
        <Dialog open={isSlaDialogOpen} onOpenChange={setIsSlaDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Configuracion de SLA</DialogTitle>
              <DialogDescription>
                Define los tiempos objetivo de resolucion por prioridad (en horas)
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveSla}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="high">Prioridad Alta (horas)</Label>
                  <Input
                    id="high"
                    type="number"
                    min="1"
                    value={slaForm.high_hours}
                    onChange={(e) => setSlaForm({ ...slaForm, high_hours: parseInt(e.target.value) || 4 })}
                    data-testid="sla-high-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="medium">Prioridad Media (horas)</Label>
                  <Input
                    id="medium"
                    type="number"
                    min="1"
                    value={slaForm.medium_hours}
                    onChange={(e) => setSlaForm({ ...slaForm, medium_hours: parseInt(e.target.value) || 24 })}
                    data-testid="sla-medium-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="low">Prioridad Baja (horas)</Label>
                  <Input
                    id="low"
                    type="number"
                    min="1"
                    value={slaForm.low_hours}
                    onChange={(e) => setSlaForm({ ...slaForm, low_hours: parseInt(e.target.value) || 48 })}
                    data-testid="sla-low-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsSlaDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={savingSla} data-testid="sla-save-btn">
                  {savingSla ? 'Guardando...' : 'Guardar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default HelpdeskReports;
